import { api } from "../../api";
import { useCallback, useMemo } from "react";
import { CompanyEntry } from "../../types/shared.ts";
import { companiesFromExternalList } from "./converts.ts";
import { useDataGridQuery, useUrlState } from "../../hooks/useUrlState.ts";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.tsx";
import { TableDialogs } from "../../components/TableDialogs.tsx";
import { ViewProvider } from "../../contexts/ViewContext.tsx";
import { COMPANIES_DEFAULTS } from "../../helpers/defaultUrl.ts";
import { ConfirmDialog } from "primereact/confirmdialog";
import CompaniesToolbar from "./CompaniesToolbar.tsx";
import { companiesConfig as config } from "./companiesConfig.tsx";
import CompaniesTable from "./CompaniesTable.tsx";
import { companiesSortFieldMap } from "../../helpers/sortFieldMap.ts";
import { DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";

const columns = config.columns;

export default function CompaniesPage() {
  const { globalFilterValue, setGlobalFilterValue } = useUrlState();
  const debouncedSearchValue = useDebouncedValue(globalFilterValue, 300);

  const { page, limit, sort, order, onPageChange, onSortChange } =
    useDataGridQuery({
      page: COMPANIES_DEFAULTS.page,
      limit: COMPANIES_DEFAULTS.limit,
      sort: COMPANIES_DEFAULTS.sort,
      order: COMPANIES_DEFAULTS.order,
    });

  //sorting
  const orderByField = useMemo(() => {
    const mapped = companiesSortFieldMap[sort];
    const base = mapped ?? COMPANIES_DEFAULTS.sort;
    const direction = order === -1 ? "-" : "";
    return `${direction}${base}`;
  }, [sort, order]);

  const { data: rawData, isLoading } =
    api.endpoints.listCompaniesApiCompaniesGet.useQuery({
      orderBy: `${orderByField}`,
      limit: limit,
      offset: page * limit,
      search: debouncedSearchValue || undefined,
      searchFields: "name,registry_code",
    });

  const data: CompanyEntry[] = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.items.map(companiesFromExternalList);
  }, [rawData]);

  const handleSortChange = useCallback(
    (e: DataTableSortEvent) => onSortChange(e.sortField, e.sortOrder ?? 1),
    [onSortChange],
  );
  const handlePageChange = useCallback(
    (e: DataTablePageEvent) => onPageChange(e.page ?? 0, e.rows ?? limit),
    [onPageChange, limit],
  );

  return (
    <ViewProvider view={config.view}>
      <TableDialogs />
      <ConfirmDialog />
      <CompaniesToolbar
        config={config}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
      />
      <CompaniesTable
        data={data}
        columns={columns}
        page={page}
        rowsPerPage={limit}
        onPage={handlePageChange}
        totalRecords={rawData?.total || 0}
        sortField={sort}
        sortOrder={order}
        onSort={handleSortChange}
        isLoading={isLoading}
      />
    </ViewProvider>
  );
}
